import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { PageHeader, CheckList } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, GitBranch, MessageCircle } from "lucide-react";
import { FlowEditor } from "@/components/FlowEditor";

const uid = () => Math.random().toString(36).slice(2, 8);
const EMPTY = () => {
  const id = uid();
  return { name: "", active: true, channel_ids: [], start_step_id: id,
    steps: [{ id, title: "Para qual local é o chamado?", type: "menu", options: [{ label: "Matriz", next_step_id: null }], queue_ids: [], field_label: "", next_step_id: null }] };
};

export default function ChatFlows() {
  const [rows, setRows] = useState([]);
  const [cat, setCat] = useState({ queues: [], sectors: [], types: [], channels: [] });
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY());

  const load = () => api.get("/chat-flows").then((r) => setRows(r.data));
  useEffect(() => {
    load();
    Promise.all([api.get("/queues"), api.get("/sectors"), api.get("/os-types"), api.get("/channels").catch(() => ({ data: [] }))])
      .then(([q, s, t, c]) => setCat({ queues: q.data, sectors: s.data, types: t.data, channels: c.data }));
  }, []);

  const openCreate = () => { setEditing(null); setForm(EMPTY()); setOpen(true); };
  const openEdit = (f) => { setEditing(f); setForm({ ...EMPTY(), ...f }); setOpen(true); };

  const submit = async () => {
    if (!form.name) return toast.error("Nome é obrigatório");
    const payload = { name: form.name, active: form.active, channel_ids: form.channel_ids, start_step_id: form.start_step_id, steps: form.steps };
    try {
      if (editing) { await api.put(`/chat-flows/${editing.id}`, payload); toast.success("Fluxo atualizado"); }
      else { await api.post("/chat-flows", payload); toast.success("Fluxo criado"); }
      setOpen(false); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const del = async (f) => {
    if (!window.confirm(`Excluir fluxo "${f.name}"?`)) return;
    await api.delete(`/chat-flows/${f.id}`); toast.success("Fluxo excluído"); load();
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-5 sm:space-y-6">
      <PageHeader title="Chat Flow" subtitle="Crie e gerencie fluxos automatizados de conversa para abertura de chamados no WhatsApp.">
        <Button asChild variant="outline"><Link to="/whatsapp" data-testid="flow-test-simulator-link"><MessageCircle className="w-4 h-4 mr-2" /> Testar no simulador</Link></Button>
        <Button onClick={openCreate} className="bg-emerald-600 hover:bg-emerald-700" data-testid="create-flow-btn"><Plus className="w-4 h-4 mr-2" /> Novo fluxo</Button>
      </PageHeader>

      {rows.length === 0 && (
        <Card className="p-4 sm:p-8 border-dashed text-center text-slate-500" data-testid="flows-empty">
          Nenhum fluxo configurado. O bot usa o fluxo automático padrão (filas → tipos de OS). Crie um fluxo para personalizar as perguntas, por exemplo: Local → Setor → Fila.
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {rows.map((f) => (
          <Card key={f.id} className="p-5 border-slate-200 dark:border-slate-800 hover:shadow-md transition-shadow" data-testid={`flow-card-${f.name}`}>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center"><GitBranch className="w-5 h-5 text-emerald-600" /></div>
                <div>
                  <h3 className="font-bold text-slate-900 dark:text-white">{f.name}</h3>
                  <p className="text-xs text-slate-500">{f.steps.length} etapa(s) · {f.channel_ids.length ? `${f.channel_ids.length} canal(is)` : "Padrão da empresa"}</p>
                </div>
              </div>
              <Badge variant="outline" className={f.active ? "text-emerald-600 border-emerald-500/30" : "text-slate-500"}>{f.active ? "Ativo" : "Inativo"}</Badge>
            </div>
            <div className="flex gap-2 mt-4">
              <Button variant="outline" size="sm" onClick={() => openEdit(f)} className="flex-1" data-testid={`edit-flow-${f.name}`}><Pencil className="w-3.5 h-3.5 mr-1" /> Editar fluxo</Button>
              <Button variant="outline" size="sm" onClick={() => del(f)} className="text-rose-600" data-testid={`delete-flow-${f.name}`}><Trash2 className="w-3.5 h-3.5" /></Button>
            </div>
          </Card>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white dark:bg-slate-900 sm:max-w-5xl w-[96vw] p-4 sm:p-6 max-h-[92vh] overflow-y-auto overflow-x-hidden">
          <DialogHeader><DialogTitle>{editing ? `Editar fluxo — ${editing.name}` : "Novo fluxo"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6">
            <div className="space-y-4">
              <div><Label>Nome *</Label><Input data-testid="flow-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ex.: Abertura por local" /></div>
              <div className="flex items-center justify-between rounded-lg border p-3 border-slate-200 dark:border-slate-800">
                <Label>Fluxo ativo</Label><Switch data-testid="flow-active-switch" checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Canais WhatsApp</p>
                <p className="text-xs text-slate-500 mb-2">Sem canal marcado = fluxo padrão da empresa.</p>
                <CheckList items={cat.channels.map((c) => ({ id: c.id, label: c.name }))} value={form.channel_ids} onChange={(v) => setForm({ ...form, channel_ids: v })} testPrefix="flow-channel" />
              </div>
              <div className="rounded-lg bg-emerald-50 dark:bg-emerald-950/30 p-3 text-xs text-emerald-800 dark:text-emerald-200 space-y-1">
                <p className="font-bold">Como funciona</p>
                <p>Cliente digita <b>1</b> no menu → percorre as etapas abaixo → informa assunto, descrição, foto e confirma.</p>
                <p>Cada opção pode levar a outra etapa e/ou definir fila, setor, tipo e prioridade da OS. Use a etapa <b>Lista de filas</b> para oferecer várias filas de uma vez.</p>
                <p>Sem resposta por 5 minutos, o bot volta ao menu.</p>
              </div>
            </div>
            <FlowEditor form={form} setForm={setForm} cat={cat} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={submit} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-flow-btn">Salvar fluxo</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
